import { importSessionMaster } from "@/helper/passwordCrypto";

/** Where the master key lives: IndexedDB can keep a non-extractable `CryptoKey`, localStorage could only keep its raw bytes. */
export interface MasterStorage {
  get(): Promise<CryptoKey | null>;
  set(key: CryptoKey): Promise<void>;
  clear(): Promise<void>;
}

const DATABASE = "ams-password-key";
const STORE = "keys";
const ENTRY = "master";

function request<T>(open: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });
}

async function openDatabase(): Promise<IDBDatabase> {
  const opening = indexedDB.open(DATABASE, 1);
  opening.onupgradeneeded = () => opening.result.createObjectStore(STORE);
  return request(opening);
}

async function inStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const database = await openDatabase();
  try {
    return await request(run(database.transaction(STORE, mode).objectStore(STORE)));
  } finally {
    database.close();
  }
}

export const indexedDbStorage: MasterStorage = {
  get: async () => (await inStore("readonly", (store) => store.get(ENTRY))) ?? null,
  set: async (key) => {
    await inStore("readwrite", (store) => store.put(key, ENTRY));
  },
  clear: async () => {
    await inStore("readwrite", (store) => store.delete(ENTRY));
  },
};

/**
 * Keeps the `encryption_key` of the login as the master of the `session` passwords and drops the raw bytes right away.
 * Any script of this origin can still use the key, it only cannot be read out.
 */
export async function storeMaster(secret: string, storage: MasterStorage = indexedDbStorage): Promise<void> {
  await storage.set(await importSessionMaster(secret));
}

/** `null` if there is none: a new device, cleared site data or a logout. */
export async function loadMaster(storage: MasterStorage = indexedDbStorage): Promise<CryptoKey | null> {
  try {
    return await storage.get();
  } catch {
    return null;
  }
}

/** Never throws: leaving a session must not fail because a browser has no IndexedDB (private windows). */
export async function clearMaster(storage: MasterStorage = indexedDbStorage): Promise<void> {
  try {
    await storage.clear();
  } catch (e) {
    console.warn("Could not remove the password key", e);
  }
}
